import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import ReactDayPicker, { DateUtils, DayModifiers } from 'react-day-picker'
import Modal from 'react-modal'

import { Button } from 'Components'
import { YearMonthForm } from 'Components/DatePicker/YearMonthForm'
import { DayJsLocaleUtils } from 'Components/DatePicker/helper'
import HeaderSmallModal from 'Components/HeaderSmallModal/HeaderSmallModal'
import { today } from 'Config'
import { modalZIndexPriority } from 'Utils/zIndexPriority'

type Range = { from?: Date; to?: Date }

type Props = {
  isOpen: boolean
  onClose: () => void
  onApply: (range: Range) => void
  initialRange?: Range
  minimumDate: Date
}

const DateRangeModal: React.FC<Props> = ({ isOpen, onClose, onApply, initialRange, minimumDate }) => {
  const [customRange, setCustomRange] = useState<Range>(initialRange ?? {})

  const [month, setMonth] = useState<Date>(initialRange?.from ?? today)

  useEffect(() => {
    if (!isOpen) {
      return
    }

    setCustomRange(initialRange ?? {})

    setMonth(initialRange?.from ?? today)
  }, [initialRange, isOpen])

  const handleCustomDayClick = (date: Date, modifiers: DayModifiers) => {
    if (modifiers.disabled) {
      return
    }

    setCustomRange((prev) => {
      if (!prev.from) {
        return { from: date, to: date }
      }

      if (prev.to) {
        if (DateUtils.isSameDay(prev.from, prev.to) && !DateUtils.isSameDay(date, prev.from)) {
          if (dayjs(date).isBefore(prev.from, 'day')) {
            return { from: date, to: prev.from }
          }

          return { from: prev.from, to: date }
        }

        return { from: date, to: date }
      }

      if (dayjs(date).isBefore(prev.from, 'day')) {
        return { from: date, to: prev.from }
      }

      return { from: prev.from, to: date }
    })
  }

  const isCustomRangeMiddle = (day: Date): boolean => {
    const { from, to } = customRange

    if (!from || !to) {
      return false
    }

    const range = { from, to }

    return DateUtils.isDayInRange(day, range) && !DateUtils.isSameDay(day, from) && !DateUtils.isSameDay(day, to)
  }

  const isCustomRangeSingle = (day: Date): boolean => {
    const { from, to } = customRange

    if (!from) {
      return false
    }

    if (!to) {
      return DateUtils.isSameDay(day, from)
    }

    return DateUtils.isSameDay(day, from) && DateUtils.isSameDay(day, to)
  }

  const isCustomRangeSingleSelection = !customRange.from
    ? false
    : !customRange.to
    ? true
    : DateUtils.isSameDay(customRange.from, customRange.to)

  return (
    <Modal style={modalZIndexPriority} isOpen={isOpen} onRequestClose={onClose}>
      <div
        className="modal is-small p-0"
        style={{
          maxWidth: '21rem'
        }}
      >
        <HeaderSmallModal onClose={onClose}>
          <h2>Zeitraum wählen</h2>
        </HeaderSmallModal>

        <div className="mx-3 mt-1 mb-3">
          <ReactDayPicker
            className="is-inline"
            locale="de-ch"
            localeUtils={DayJsLocaleUtils}
            month={month}
            fromMonth={minimumDate}
            toMonth={today}
            selectedDays={
              customRange.from && customRange.to ? { from: customRange.from, to: customRange.to } : customRange.from
            }
            modifiers={{
              rangeSingle: isCustomRangeSingle,
              rangeStart: isCustomRangeSingleSelection ? undefined : customRange.from,
              rangeEnd: isCustomRangeSingleSelection ? undefined : customRange.to,
              rangeMiddle: isCustomRangeMiddle
            }}
            modifiersStyles={{
              rangeSingle: {
                borderRadius: '4px'
              },
              rangeStart: {
                borderRadius: '4px 0 0 4px'
              },
              rangeMiddle: {
                borderRadius: '0'
              },
              rangeEnd: {
                borderRadius: '0 4px 4px 0'
              }
            }}
            onDayClick={handleCustomDayClick}
            firstDayOfWeek={1}
            onMonthChange={setMonth}
            captionElement={({ date, localeUtils }) => (
              <YearMonthForm date={date} localeUtils={localeUtils} onChange={setMonth} />
            )}
            disabledDays={{ before: minimumDate, after: today }}
          />

          <div className="mt-3">
            <Button type="button" onClick={() => onApply(customRange)} disabled={!customRange.from || !customRange.to}>
              Anwenden
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default DateRangeModal
